import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@crisellevaldez/speculo-ui';
import { notifyCredentialConnected, notifyCredentialDeleted } from '../../api/rollout';
import {
  connectProductionCrm,
  activateProductionCrmSync,
  disconnectProductionCrm,
  getProductionCrmConnectionStatus,
  isManagedCrmApp,
} from '../../api/productionCrmConnections';
import { updateOrganization } from '../../api/remiApi';
import { Organization } from '../../types/organization.types';

interface UseRolloutCrmConnectionOptions {
  /** The selected organization */
  selectedOrg?: Organization;
  /** Callback when connection/disconnection succeeds */
  onSuccess?: () => void;
  /** Captured modal/auth context, checked before effects and after each await. */
  isCurrent?: () => boolean;
}

interface CredentialPayload {
  id?: string;
  appKey: string;
  subdomain?: string;
}

/**
 * Converts appKey to a normalized key for storage
 * e.g., 'follow-up-boss' -> 'fub', 'sierra-interactive' -> 'sierra'
 */
const normalizeAppKey = (appKey: string): string => {
  const keyMap: Record<string, string> = {
    'follow-up-boss': 'fub',
    'sierra-interactive': 'sierra',
    'cinc': 'cinc',
    'cinc-pro': 'cinc',
    'brivity': 'brivity',
    'kvcore': 'brivity',
    'boldtrail': 'boldtrail',
    'boldtrail-oauth': 'boldtrail',
  };
  return keyMap[appKey] || appKey.replace(/-/g, '_');
};

/**
 * Converts appKey to a human-readable name for toast messages
 */
const getAppDisplayName = (appKey: string): string => {
  const nameMap: Record<string, string> = {
    'follow-up-boss': 'Follow Up Boss',
    'sierra-interactive': 'Sierra Interactive',
    'cinc': 'CINC',
    'cinc-pro': 'CINC',
    'brivity': 'Brivity',
    'kvcore': 'Brivity',
    'boldtrail': 'BoldTrail',
    'boldtrail-oauth': 'BoldTrail',
    'sure-send': 'Sure Send',
    'salesforce': 'Salesforce',
  };
  return nameMap[appKey] || appKey.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Hook for managing CRM connection state via Rollout
 * Handles connecting, disconnecting, and persisting state via Remi API
 * Supports any CRM dynamically based on appKey
 *
 * @example
 * const { handleCredentialAdded, handleCredentialDeleted, isConnecting, isDisconnecting } = useRolloutCrmConnection({
 *   selectedOrg,
 *   onSuccess: () => refetch(),
 * });
 */
export const useRolloutCrmConnection = ({
  selectedOrg,
  onSuccess,
  isCurrent,
}: UseRolloutCrmConnectionOptions) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Helper to update organization's CRM connection status via Remi API
  const updateOrgCrmStatus = async (
    orgId: string,
    appKey: string,
    connected: boolean,
    subdomain?: string
  ) => {
    const crmKey = normalizeAppKey(appKey);
    const existingRollout = selectedOrg?.rollout || {};
    const existingCrm =
      ((existingRollout as Record<string, unknown>)[crmKey] as Record<string, unknown>) || {};

    const crmUpdate = connected
      ? {
          ...existingCrm,
          connected: true,
          connectedAt: new Date().toISOString(),
          ...(subdomain ? { subdomain } : {}),
        }
      : {
          connected: false,
          disconnectedAt: new Date().toISOString(),
        };

    await updateOrganization(orgId, {
      rolloutConfig: {
        ...existingRollout,
        [crmKey]: crmUpdate,
      },
    });
  };

  // CONNECT mutation
  const connectMutation = useMutation({
    mutationFn: async ({ id, appKey, subdomain }: CredentialPayload) => {
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      if (!selectedOrg?.id) throw new Error('Organization ID is required');
      if (!id) throw new Error('CRM credential ID is required');
      const orgId = selectedOrg.id;

      if (import.meta.env.VITE_APP_ENV === 'production' && isManagedCrmApp(appKey)) {
        const result = await connectProductionCrm(orgId, id, appKey);
        if (result.success !== true || result.organization_id !== orgId ||
            result.app !== appKey || result.status !== 'active') {
          throw new Error('CRM connection confirmation unavailable');
        }
        if (isCurrent && !isCurrent()) return { id, appKey, syncUnconfirmed: true };
        const bound = await getProductionCrmConnectionStatus(orgId);
        if (!['connected', 'sync_paused'].includes(bound.status) || bound.app !== appKey ||
            typeof bound.sync_eligible !== 'boolean') {
          throw new Error('CRM connection readback unavailable');
        }
        let syncUnconfirmed = !bound.sync_eligible;
        if (bound.sync_eligible && (!isCurrent || isCurrent())) {
          try {
            const sync = await activateProductionCrmSync(orgId, appKey, 'bidirectional');
            syncUnconfirmed = sync.status !== 'ok';
          } catch {
            // The binding is already committed. A paused or uncertain sync result
            // must never trigger a second SDK Save or a second org-config write.
            syncUnconfirmed = true;
          }
        }
        const current = await getProductionCrmConnectionStatus(orgId);
        if (!['connected', 'sync_paused'].includes(current.status) || current.app !== appKey ||
            typeof current.sync_enabled !== 'boolean') {
          throw new Error('CRM connection readback unavailable');
        }
        return { id, appKey, syncUnconfirmed: syncUnconfirmed ||
          current.status === 'sync_paused' || current.sync_direction !== 'bidirectional' };
      }

      // Existing FUB and historical non-managed connectors retain their setup contract.
      await notifyCredentialConnected({
        credential_id: id,
        app: appKey,
        organization_id: orgId,
      });

      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      await updateOrgCrmStatus(orgId, appKey, true, subdomain);

      return { id, appKey };
    },
    onSuccess: (data) => {
      if (isCurrent && !isCurrent()) return;
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      const displayName = getAppDisplayName(data.appKey);
      showToast(data.syncUnconfirmed
        ? { message: `${displayName} connected; CRM sync is paused or unconfirmed.`, variant: 'info' }
        : { message: `${displayName} connected successfully!`, variant: 'success' });

      onSuccess?.();
    },
    onError: () => {
      if (isCurrent && !isCurrent()) return;
      showToast({
        message: 'Failed to connect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // DISCONNECT mutation
  const disconnectMutation = useMutation({
    mutationFn: async ({ id, appKey }: CredentialPayload) => {
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      if (!selectedOrg?.id) throw new Error('Organization ID is required');
      if (!id) throw new Error('CRM credential ID is required');
      const orgId = selectedOrg.id;

      if (import.meta.env.VITE_APP_ENV === 'production' && isManagedCrmApp(appKey)) {
        const result = await disconnectProductionCrm(orgId, id);
        if (result.success !== true || result.organization_id !== orgId ||
            result.status !== 'disconnected') {
          throw new Error('CRM disconnection confirmation unavailable');
        }
        let cleanupUnconfirmed = false;
        try {
          await notifyCredentialDeleted({ credential_id: id, organization_id: orgId });
        } catch {
          // The row is already disconnected; retain that truthful state even
          // when webhook cleanup needs a separate retry.
          cleanupUnconfirmed = true;
        }
        const current = await getProductionCrmConnectionStatus(orgId);
        if (current.status !== 'not_connected') {
          throw new Error('CRM disconnection readback unavailable');
        }
        return { id, appKey, cleanupUnconfirmed };
      }

      await notifyCredentialDeleted({
        credential_id: id,
        organization_id: orgId,
      });

      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      await updateOrgCrmStatus(orgId, appKey, false);

      return { id, appKey };
    },
    onSuccess: (data) => {
      if (isCurrent && !isCurrent()) return;
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      const displayName = getAppDisplayName(data.appKey);
      showToast({
        message: data.cleanupUnconfirmed
          ? `${displayName} disconnected; CRM webhook cleanup is unconfirmed.`
          : `${displayName} disconnected successfully`,
        variant: 'info',
      });
      onSuccess?.();
    },
    onError: () => {
      if (isCurrent && !isCurrent()) return;
      showToast({
        message: 'Failed to disconnect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // Handlers to be passed to CredentialsManager callbacks
  const handleCredentialAdded = async (credential: CredentialPayload) => {
    await connectMutation.mutateAsync(credential);
  };

  const handleCredentialDeleted = async (credential: CredentialPayload) => {
    await disconnectMutation.mutateAsync(credential);
  };

  return {
    /** Handler for CredentialsManager onCredentialAdded callback */
    handleCredentialAdded,
    /** Handler for CredentialsManager onCredentialDeleted callback */
    handleCredentialDeleted,
    /** Whether a connection is in progress */
    isConnecting: connectMutation.isPending,
    /** Whether a disconnection is in progress */
    isDisconnecting: disconnectMutation.isPending,
    /** Connection error if any */
    connectError: connectMutation.error,
    /** Disconnection error if any */
    disconnectError: disconnectMutation.error,
  };
};
